// =============================================================
// PowerSync offline schema — the client-side SQLite views that PowerSync
// replicates into (web = Wasm-SQLite/OPFS, native = OP-SQLite).
//
// This is the LOCAL (on-device) schema, NOT the Postgres system-of-record
// schema (that lives in @perpetuum/server/db/schema.ts). PowerSync replicates
// a per-user SUBSET of Postgres into these SQLite-backed views via Sync Rules:
//   - asset tree (3 levels: locations + devices + the device's intervals)
//   - assigned tasks + their sub-data (checklist / spares / work_times)
//   - spares catalog + per-warehouse stock levels
//   - teams / technicians (users + groups)
//
// CON-domain-offline (BINDING): "Dexie/IndexedDB cache of asset tree (3 levels),
// assigned tasks, spares catalog, technicians/teams" — now PowerSync over SQLite
// on BOTH surfaces (PROJECT.md offline-store decision; the platform split is gone).
//
// PowerSync's local store is SQLite-relational (TEXT/INTEGER/REAL only). Postgres
// JSONB columns (devices.custom_fields, assigned_user_ids) land as TEXT we
// JSON.parse in core. Every local row has an implicit `id` TEXT primary key, so
// it is not declared as a column here (PowerSync supplies it).
// =============================================================

import { Column, ColumnType, Schema, Table } from "@powersync/common";

/** Helper: a TEXT column. */
const text = (name: string) => new Column({ name, type: ColumnType.TEXT });
/** Helper: an INTEGER column (booleans are stored 0/1). */
const int = (name: string) => new Column({ name, type: ColumnType.INTEGER });
/** Helper: a REAL column (floats — work hours, quantities, costs). */
const real = (name: string) => new Column({ name, type: ColumnType.REAL });

// ---------------------------------------------------------------------------
// Asset tree (3 levels) — locations → devices → maintenance intervals.
// ---------------------------------------------------------------------------

export const locationsTable = new Table({
  name: "locations",
  columns: [
    text("company_id"),
    text("alldevice_id"),
    text("name"),
    text("parent_id"),
    text("objects_path"),
    text("department_id"),
    text("updated_at"),
  ],
  indexes: [],
});

export const devicesTable = new Table({
  name: "devices",
  columns: [
    text("company_id"),
    text("alldevice_id"),
    text("name"),
    text("status"),
    text("category_id"),
    text("location_id"),
    text("parent_device_id"),
    text("serial_number"),
    text("inventory_number"),
    real("work_hours"),
    text("nfc_tag_uid"),
    // Postgres JSONB → TEXT here; parsed in core via parseCustomFields().
    text("custom_fields"),
    text("updated_at"),
  ],
  indexes: [],
});

export const maintenanceIntervalsTable = new Table({
  name: "maintenance_intervals",
  columns: [
    text("company_id"),
    text("alldevice_id"),
    text("device_id"),
    text("name"),
    int("calendar_days"),
    real("counter_hours"),
    real("break_in_hours"),
    text("updated_at"),
  ],
  indexes: [],
});

// ---------------------------------------------------------------------------
// Assigned tasks + sub-data (checklist / spares / work_times).
// ---------------------------------------------------------------------------

export const tasksTable = new Table({
  name: "tasks",
  columns: [
    text("company_id"),
    text("alldevice_id"),
    text("number"),
    text("title"),
    text("device_id"),
    text("location_id"),
    text("status"),
    text("priority"),
    text("service_name"),
    text("description"),
    text("interval_id"),
    int("due_days"),
    int("is_completed"),
    text("due_date"),
    text("completed_at"),
    // JSONB array of user ids → TEXT here.
    text("assigned_user_ids"),
    text("template_id"),
    int("loto_confirmed"),
    int("signed"),
    text("verified_by"),
    text("updated_at"),
  ],
  indexes: [],
});

export const taskChecklistItemsTable = new Table({
  name: "task_checklist_items",
  columns: [
    text("task_id"),
    text("label"),
    int("done"),
    int("sort_order"),
    text("updated_at"),
  ],
  indexes: [],
});

export const taskSparesTable = new Table({
  name: "task_spares",
  columns: [
    text("task_id"),
    text("spare_id"),
    real("booked"),
    real("used"),
    text("updated_at"),
  ],
  indexes: [],
});

export const taskWorkTimesTable = new Table({
  name: "task_work_times",
  columns: [
    text("task_id"),
    text("user_id"),
    text("started_at"),
    text("ended_at"),
    int("duration_ms"),
    text("updated_at"),
  ],
  indexes: [],
});

// ---------------------------------------------------------------------------
// Spares catalog + per-warehouse stock levels.
// ---------------------------------------------------------------------------

export const sparesTable = new Table({
  name: "spares",
  columns: [
    text("company_id"),
    text("alldevice_id"),
    text("code"),
    text("name"),
    text("category_id"),
    real("min_qty"),
    real("unit_cost"),
    text("updated_at"),
  ],
  indexes: [],
});

export const stocksTable = new Table({
  name: "stocks",
  columns: [
    text("company_id"),
    text("alldevice_id"),
    text("name"),
    int("is_main"),
    text("updated_at"),
  ],
  indexes: [],
});

export const stockLevelsTable = new Table({
  name: "stock_levels",
  columns: [
    text("spare_id"),
    text("stock_id"),
    real("in_stock"),
    real("booked"),
    text("updated_at"),
  ],
  indexes: [],
});

// ---------------------------------------------------------------------------
// Teams / technicians — users + groups (for assignment + RBAC display).
// ---------------------------------------------------------------------------

export const usersTable = new Table({
  name: "users",
  columns: [
    text("company_id"),
    text("alldevice_id"),
    text("name"),
    text("lang"),
    text("department_id"),
    // JSONB array of group ids → TEXT here.
    text("group_ids"),
    int("is_active"),
    text("updated_at"),
  ],
  indexes: [],
});

export const userGroupsTable = new Table({
  name: "user_groups",
  columns: [
    text("company_id"),
    text("alldevice_id"),
    text("name"),
    text("updated_at"),
  ],
  indexes: [],
});

/**
 * The complete PowerSync offline schema. ONE definition shared by web + native;
 * the only thing that differs between platforms is the storage backend (the
 * SQLite adapter passed to the client), never this schema.
 */
export const powerSyncSchema = new Schema([
  locationsTable,
  devicesTable,
  maintenanceIntervalsTable,
  tasksTable,
  taskChecklistItemsTable,
  taskSparesTable,
  taskWorkTimesTable,
  sparesTable,
  stocksTable,
  stockLevelsTable,
  usersTable,
  userGroupsTable,
]);

/** The set of table names that are replicated offline (handy for tests/tooling). */
export const SYNCED_TABLES = [
  "locations",
  "devices",
  "maintenance_intervals",
  "tasks",
  "task_checklist_items",
  "task_spares",
  "task_work_times",
  "spares",
  "stocks",
  "stock_levels",
  "users",
  "user_groups",
] as const;

export type SyncedTableName = (typeof SYNCED_TABLES)[number];

/**
 * Parse a TEXT column that holds JSON (Postgres JSONB replicated as text).
 * Returns the fallback when the column is null/empty/invalid. Used in core for
 * `devices.custom_fields`, `tasks.assigned_user_ids`, `users.group_ids`.
 */
export function parseJsonColumn<T>(value: string | null | undefined, fallback: T): T {
  if (value == null || value === "") return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}
